import { useState, useEffect, useContext, useRef } from 'react';

import styles from './ChatComponent.module.css';

import { GlobalContext } from '../../contexts/globalContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-fe6b9b7683/icons';

import InitialsCircleComponent from '../commons/InitialsCircleComponent';
import CustomMultitouchComponent from '../../files/CustomMultitouchComponent';
import TextAreaComponent from '../keyboard/TextAreaComponent';

import ChatImg from '../../graphics/conferenceScreen/chat-img.png';
import botAnimation from '../../graphics/bot.json'
import botIcon from "../../graphics/conferenceScreen/bot-shape.svg"
import resizeButton from '../../graphics/conferenceScreen/note/expand.svg'
import emojis from '../../langs/emojiUnicodeMap.json'

import { marked } from 'marked';

import Lottie from 'react-lottie-player';



const ChatComponent = ({ open, setOpenChat }) => {

    const { socketRef, currentParticipant, appWidth, appHeight, setHasUnreadMessages, keyboardRef, participantsStreams } = useContext(GlobalContext);

    const inputRef = useRef();
    const mtRef = useRef();
    const emojiRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const thinkingRef = useRef();
    const aiResponseRef = useRef();
    const messagesEndRef = useRef();
    const currentReceiverRef = useRef()
    const isOpenCanvasRef = useRef(false);

    const [messages, setMessages] = useState({});
    const [input, setInput] = useState('');
    const [currentReceiver, setCurrentReceiver] = useState('');
    const [showEmoji, setShowEmoji] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState({});
    const [streamingMessage, setStreamingMessage] = useState("");
    const [thinking, setThinking] = useState(false);
    const [waitingForResponse, setWaitingForResponse] = useState(false);

    const aiUser = { _id: 'ai-bot', name: 'Bot AI' };

    const users = participantsStreams
        .filter(stream => stream.participant && stream.participant.name)
        .map(stream => stream.participant);

    const allUsers = [aiUser, ...users];

    const options = {
        appWidth,
        appHeight,
        maxScale: 1.25,
        minScale: 0.5
    };

    const emojiList = Object.keys(emojis)

    const emojiUnicodeMap = emojis;

    const handleEmojiClick = (emojiChar) => {
        const currentValue = inputRef.current?.value || '';
        inputRef.current.value = currentValue + emojiChar;
        setInput(inputRef.current.value);
        inputRef.current.focus();
    };

    const renderEmojiInMessage = (message) => {
        const emojiRegex = /:([a-zA-Z0-9-]+):/g;
        return message.split(' ').map((word, index) => {
            const match = word.match(emojiRegex);
            if (match) {
                const iconName = match[0].slice(1, -1);
                const emojiChar = emojiUnicodeMap[iconName];
                if (emojiChar) {
                    return <span style={{ padding: 0, margin: 0, wordSpacing: 0 }} key={index}>{emojiChar}</span>;
                }
            }
            return <span key={index} style={{ padding: 0, margin: 0, wordSpacing: 0 }} >{word} </span>;
        });
    };

    useEffect(() => {
        const handleMessage = (sender, receiver, message) => {
            if (receiver === currentParticipant?._id) {
                const senderId = sender._id;

                setMessages((prev) => ({
                    ...prev,
                    [senderId]: [...(prev[senderId] || []), `${sender.name}: ${message}`]
                }));

                if (!currentReceiver || currentReceiver._id !== senderId) {
                    setUnreadMessages((prev) => ({
                        ...prev,
                        [senderId]: (prev[senderId] || 0) + 1 // ✅ increase unread count
                    }));

                    setHasUnreadMessages(Object.values(unreadMessages).filter(Boolean).length)
                }
            }
        };

        socketRef.current.on('CONFERENCE_SCREEN_RECEIVING_MESSAGE', handleMessage);

        return () => {
            socketRef.current.off('CONFERENCE_SCREEN_RECEIVING_MESSAGE', handleMessage);
        };
    }, [currentParticipant?._id, currentReceiver, socketRef]);

    useEffect(() => {
        currentReceiverRef.current = currentReceiver
    }, [currentReceiver])

    useEffect(() => {
        if (!currentReceiver) return;

        const stillExists = allUsers.some(user => user._id === currentReceiver._id);

        if (!stillExists) {
            setCurrentReceiver('');
        }
    }, [allUsers, currentReceiver]);

    const sendMessage = async () => {

        const message = inputRef.current?.value?.trim();

        if (!message || !currentReceiver) return;

        const receiverId = currentReceiverRef.current._id;

        // Show user's message immediately
        setMessages((prev) => ({
            ...prev,
            [receiverId]: [...(prev[receiverId] || []), `You: ${message}`]
        }));

        setUnreadMessages((prev) => ({
            ...prev,
            [receiverId]: false
        }));

        // inputRef.current.value = '';
        setInput('');
        inputRef.current.focus()

        // Handle AI bot response
        if (receiverId === 'ai-bot') {
            socketRef.current.emit('AI_ASK', { userId: currentParticipant?._id, text: message });
            setWaitingForResponse(true);
            setStreamingMessage();
            aiResponseRef.current = "";
            setThinking(true);
        } else {
            // Normal user-to-user message
            socketRef.current.emit('CONFERENCE_SCREEN_SEND_MESSAGE', {
                msg: message,
                receiver: receiverId,
                sender: currentParticipant
            });
        }
    };

    const handleUserClick = (participant) => {
        setCurrentReceiver(participant._id === currentReceiver._id ? '' : participant);
        setUnreadMessages((prev) => ({
            ...prev,
            [participant._id]: false
        }));
        if (inputRef.current) {
            inputRef.current.value = '';
        }

        setInput('');
    };

    const stopStreaming = () => {
        socketRef.current.emit('AI_ASK_STOP', { userId: currentParticipant?._id });
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [messages, streamingMessage, currentReceiver]);

    useEffect(() => {
        const count = Object.values(unreadMessages).filter(Boolean).length;
        setHasUnreadMessages(count);
    }, [unreadMessages]);

    useEffect(() => {
        if (!open) {
            setCurrentReceiver("");
            widthRef.current = initialWidthRef.current;
            heightRef.current = initialHeightRef.current;
            setWidth(initialWidthRef.current);
            setHeight(initialHeightRef.current);
        }

    }, [open]);

    const onMinScaleReached = () => {
        setOpenChat(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                emojiRef.current &&
                !emojiRef.current.contains(event.target) &&
                !emojiButtonRef.current.contains(event.target)
            ) {
                setShowEmoji(false);
            }
        };

        if (showEmoji) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmoji]);

    useEffect(() => {
        thinkingRef.current = thinking;
    }, [thinking]);

    useEffect(() => {
        socketRef.current.on("AI_RESPONSE", (response) => {
            if (response.userId === currentParticipant?._id) {
                if (response.done === false) {
                    setThinking(false);
                    aiResponseRef.current += response.text;
                    setStreamingMessage(aiResponseRef.current);
                } else {
                    setWaitingForResponse(false);
                    if (aiResponseRef.current !== "") {
                        setMessages((prev) => ({
                            ...prev,
                            ['ai-bot']: [...(prev['ai-bot'] || []), `'ai-bot': ${aiResponseRef.current}`]
                        }));
                    }
                    setStreamingMessage();
                    setThinking(false);
                }
            }
        });

        socketRef.current.on("AI_ASK_STOPPED", (response) => {
            if (response.userId === currentParticipant?._id) {
                setWaitingForResponse(false);
                if (aiResponseRef.current !== "") {
                    setMessages((prev) => ({
                        ...prev,
                        ['ai-bot']: [...(prev['ai-bot'] || []), `'ai-bot': ${aiResponseRef.current}`]
                    }));
                }
                setStreamingMessage();
                setThinking(false);
            }
        });

        window.addEventListener('pointerup', windowPointerUp)
        window.addEventListener('pointermove', resizePointerMove)

        return () => {
            socketRef.current.off("AI_RESPONSE");
            socketRef.current.off("AI_ASK_STOPPED");
            window.removeEventListener('pointerup', windowPointerUp)
            window.removeEventListener('pointermove', resizePointerMove)
        }
    }, []);

    const pointerLastX = useRef();
    const pointerLastY = useRef();
    const isResizingRef = useRef(false);
    const initialWidthRef = useRef(1778);
    const initialHeightRef = useRef(980);
    const widthRef = useRef(initialWidthRef.current);
    const heightRef = useRef(initialHeightRef.current);

    const [width, setWidth] = useState(initialWidthRef.current);
    const [height, setHeight] = useState(initialHeightRef.current);

    // drag baseline (size at pointerDown)
    const dragBaseWRef = useRef(width);
    const dragBaseHRef = useRef(height);
    // virtual un-clamped deltas since pointerDown
    const dragDXRef = useRef(0);
    const dragDYRef = useRef(0);
    const currentScaleRef = useRef(1);

    const MAX_W = initialWidthRef.current * 1.8;
    const MAX_H = initialHeightRef.current * 1.8;
    const MIN_W = initialWidthRef.current;
    const MIN_H = initialHeightRef.current;

    const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

    const resizePointerDown = (e) => {
        isResizingRef.current = true;
        pointerLastX.current = e.screenX;
        pointerLastY.current = e.screenY;

        // lock the baseline to the displayed size at drag start
        dragBaseWRef.current = width;
        dragBaseHRef.current = height;

        // reset virtual deltas
        dragDXRef.current = 0;
        dragDYRef.current = 0;
    };

    const oxDivider = window.innerWidth / appWidth;
    const oyDivider = window.innerHeight / appHeight;

    const resizePointerMove = (e) => {
        if (!isResizingRef.current) return;

        const dx = (e.screenX - pointerLastX.current) / oxDivider / currentScaleRef.current;
        const dy = (e.screenY - pointerLastY.current) / oyDivider / currentScaleRef.current;

        // accumulate *virtual* deltas since drag start
        dragDXRef.current += dx;
        dragDYRef.current += dy;

        // compute proposed (unclamped) sizes from the drag baseline
        const proposedW = dragBaseWRef.current + dragDXRef.current;
        const proposedH = dragBaseHRef.current + dragDYRef.current;

        // clamp only for display
        const nextW = clamp(proposedW, MIN_W, MAX_W);
        const nextH = clamp(proposedH, MIN_H, MAX_H);

        widthRef.current = nextW;
        heightRef.current = nextH;
        setWidth(nextW);
        setHeight(nextH);

        pointerLastX.current = e.screenX;
        pointerLastY.current = e.screenY;
    };

    const windowPointerUp = () => {
        isResizingRef.current = false;
    };

    const onPinch = (currentScale) => {
        currentScaleRef.current = currentScale;
    }




    return (
        <CustomMultitouchComponent ref={mtRef} dragGesture={false} x={1921} y={-1083} open={open} dragGestureMouse={true} dragBox={{ x: 0, y: 0, width: MAX_W, height: 84 }}
            onMinScaleReached={onMinScaleReached} onPinch={onPinch} options={options} reposition={true} isOpenCanvasRef={isOpenCanvasRef}>
            <div className={styles.chatContainer} style={{ display: open ? 'flex' : 'none', width: `${width}px`, height: `${height}px` }}>
                <FontAwesomeIcon className={styles.icon} onClick={() => { setOpenChat(false) }} icon={byPrefixAndName.fal['close']} />
                <div className={styles.leftPanel}>
                    <div className={styles.chatLabel}>
                        <FontAwesomeIcon
                            className={styles.dropdownIcon}
                            style={{
                                marginLeft: 30,
                                marginRight: 30,
                                color: '#716FA8',
                                font: "normal normal 300 32px/64px",
                            }}
                            icon={byPrefixAndName.fal["messages"]}
                        />
                        Chat
                    </div>
                    <div className={styles.userList}>

                        {allUsers.map((participant) => (
                            <div
                                key={participant._id}
                                className={`${styles.userItem} ${currentReceiver._id === participant._id ? styles.selectedUser : ''}`}
                                onClick={() => handleUserClick(participant)}
                            >
                                {participant._id !== 'ai-bot' && (
                                    <InitialsCircleComponent
                                        name={participant.name}
                                        style={{
                                            marginLeft: 10,
                                            height: 70,
                                            width: 70,
                                            marginTop: 0,
                                            border: "4px solid #0D0032",
                                            font: 'normal normal normal 32px / 39px "bio-sans"',
                                            position: 'relative',
                                            flexShrink: 0
                                        }}
                                    />)}
                                {participant._id === 'ai-bot' && (
                                    <div style={{ position: 'relative', width: 80, height: 80, marginLeft: 10 }}>
                                        <Lottie
                                            loop
                                            play
                                            animationData={botAnimation}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                background: 'transparent',
                                            }}
                                        />
                                        <img
                                            src={botIcon}
                                            style={{
                                                position: 'absolute',
                                                top: 16,
                                                left: 16,
                                                height: 45,
                                                pointerEvents: 'none',
                                                mixBlendMode: 'screen'
                                            }}
                                        />
                                    </div>

                                )}
                                <div className={styles.participantName}>{participant.name}</div>
                                {unreadMessages[participant._id] > 0 && (
                                    <div className={styles.unreadIndicator}>
                                        <div className={styles.unreadIndicatorText}>{unreadMessages[participant._id]}</div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className={styles.participantListShadow_bottom} style={{ pointerEvents: 'none' }} />
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', width: "calc(100% - 578px)", flex: "0 0 auto" }}>
                    {currentReceiver.name && (
                        <div className={styles.chatLabel}>
                            <label style={{ marginLeft: 30 }}>{currentReceiver.name}</label>
                        </div>
                    )}
                    {!currentReceiver.name && (
                        <div className={styles.noReceiver}>
                            <img src={ChatImg} className={styles.image} />
                            <label className={styles.noReceiverLabel}> Start a conversation </label>
                        </div>
                    )}
                    <div className={styles.messageBox}>
                        {(messages[currentReceiver._id] || []).map((msg, index) => {
                            var content = msg.includes(":") ? msg.split(":").slice(1).join(":").trim() : msg;
                            if (currentReceiver._id !== 'ai-bot') {
                                content = renderEmojiInMessage(content)
                            }
                            return (
                                currentReceiver._id === 'ai-bot' ? (

                                    <div
                                        key={index}
                                        id="ai-response"
                                        className={msg.startsWith("You:") ? styles.messageSent : styles.messageRecieved}
                                        dangerouslySetInnerHTML={{ __html: marked.parse(content) }}
                                    />

                                ) : (
                                    <div
                                        key={index}
                                        className={msg.startsWith("You:") ? styles.messageSent : styles.messageRecieved}
                                    >
                                        {content}
                                    </div>
                                )
                            );
                        })}
                        {streamingMessage && (
                            <div
                                className={styles.messageRecieved}
                                dangerouslySetInnerHTML={{ __html: marked.parse(streamingMessage) }}
                            />
                        )}
                        {thinking && currentReceiver._id === 'ai-bot' && <div className={styles.thinkingMessage}>Thinking ...</div>}
                        <div ref={messagesEndRef} />
                    </div>

                    {currentReceiver.name && (
                        <div className={styles.inputBox}>

                            <TextAreaComponent
                                ref={inputRef}
                                keyboardRef={keyboardRef}
                                value={input}
                                height={80}
                                fontSize="32px"
                                marginLeft={20}
                                width="calc(100% - 75px)"
                                padding={20}
                                placeHolder={"Type a message"}
                                styleType='CHAT'
                                onEnterClicked={sendMessage}
                                onChange={(val) => setInput(val)}
                                withDelete={true}
                                isChat={true}
                                maxRows={4}
                            />
                            <FontAwesomeIcon className={styles.smileFace} ref={emojiButtonRef} icon={byPrefixAndName.fal['face-smile']} onClick={() => setShowEmoji(!showEmoji)} />
                            {!waitingForResponse && <div className={`${styles.sendButton} ${!input.trim() ? styles.inactiveArrowUp : ''}`} onClick={sendMessage}>
                                <FontAwesomeIcon
                                    style={{
                                        color: !input.trim() ? "#ffffff80" : "#050020",
                                        font: "normal normal normal 28px/35px",
                                        width: 28,
                                        height: 35,
                                    }}
                                    icon={byPrefixAndName.fas['arrow-up']}
                                />
                            </div>}

                            {waitingForResponse && <div className={`${styles.sendButton} ${!input.trim() ? styles.inactiveArrowUp : ''}`} onClick={stopStreaming}>

                                <FontAwesomeIcon
                                    style={{
                                        color: "#ffffff80",
                                        font: "normal normal normal 28px/35px",
                                        width: 28,
                                        height: 35
                                    }}
                                    icon={byPrefixAndName.fas['stop']}
                                />
                            </div>}
                        </div>
                    )}
                </div>
                {showEmoji && (
                    <div className={styles.emojiDiv} ref={emojiRef} >
                        {emojiList.map((iconName, idx) => (
                            <span
                                key={idx}
                                className={styles.emoji}
                                onClick={() => handleEmojiClick(emojiUnicodeMap[iconName])}
                            >{emojiUnicodeMap[iconName]}</span>
                        ))}
                    </div>
                )}
                <div className={styles.resizeButton} onPointerDown={resizePointerDown}>
                    <img src={resizeButton} alt={"resizeButton"} draggable={false} />
                </div>
            </div>
        </CustomMultitouchComponent >
    );
};

export default ChatComponent;